import { hostname } from "./api";
import jwt_decode from "jwt-decode";

let refreshTokenTimeout;
let isRefreshing = false;
let pendingRequests = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_MARGIN_MS = 2 * 60 * 1000;

const authProvider = (tokenOps) => {
  // Función para limpiar completamente el estado de autenticación
  const cleanupAuthState = (reason = new Error("Sesión finalizada.")) => {
    clearTimeout(refreshTokenTimeout);
    refreshTokenTimeout = null;
    refreshAttempts = 0;
    isRefreshing = false;
    pendingRequests.forEach(req => req.reject(reason));
    pendingRequests = [];
    tokenOps.removeRootToken();
  };

  const startRefreshTokenTimer = (accessToken) => {
    // Limpiar cualquier temporizador existente
    if (refreshTokenTimeout) {
      clearTimeout(refreshTokenTimeout);
      refreshTokenTimeout = null;
    }

    try {
      const jwtToken = jwt_decode(accessToken);
      const expires = new Date(jwtToken.exp * 1000);
      const timeout = expires.getTime() - Date.now() - REFRESH_MARGIN_MS;

      // No configurar temporizadores negativos
      if (timeout <= 0) {
        console.warn('[Auth] Token ya cerca de expiración, renovando inmediatamente');
        void refreshAccessToken().catch(() => undefined);
        return;
      }

      refreshTokenTimeout = setTimeout(() => {
        void refreshAccessToken().catch(() => undefined);
      }, timeout);
    } catch (error) {
      console.error('[Auth] Error al configurar temporizador de renovación:', error);
    }
  };

  const refreshAccessToken = async () => {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject });
      });
    }

    isRefreshing = true;

    // Determinar qué token de actualización usar basado en el contexto
    const isAccountSwitched = tokenOps.isSwitchAccount;
    const refreshToken = isAccountSwitched
      ? tokenOps.account?.refreshToken
      : tokenOps.root?.refreshToken;

    if (!refreshToken) {
      console.error('[Auth] No hay refresh token disponible');
      isRefreshing = false;
      cleanupAuthState(); // Usar la función centralizada
      return Promise.reject(new Error("Sesión expirada. No hay refresh token."));
    }

    try {
      const response = await fetch(`${hostname}/auth/refresh`, {
        method: "POST",
        body: JSON.stringify({ refreshToken }),
        headers: new Headers({ "Content-Type": "application/json" }),
      });

      if (!response.ok) {
        console.warn(`[Auth] Renovación falló con estado: ${response.status}`);
        const refreshError = new Error(response.statusText || 'Error al renovar token');
        if (response.status === 401 || response.status === 403) {
          console.error('[Auth] Refresh token rechazado por el servidor (401/403)');
          refreshError.isTerminalAuthFailure = true;
          cleanupAuthState(refreshError);
        }
        throw refreshError;
      }

      const auth = await response.json();

      refreshAttempts = 0;

      if (isAccountSwitched) {
        tokenOps.setAccountToken(auth);
      } else {
        tokenOps.setRootToken(auth);
      }

      startRefreshTokenTimer(auth.accessToken);

      pendingRequests.forEach((req) => req.resolve(auth.accessToken));
      pendingRequests = [];
      isRefreshing = false;
      return auth.accessToken;
    } catch (error) {
      console.error('[Auth] Error al renovar token:', error);

      refreshAttempts++;
      isRefreshing = false;

      if (error.isTerminalAuthFailure) {
        return Promise.reject(error);
      }

      if (refreshAttempts < MAX_REFRESH_ATTEMPTS) {
        const backoffTime = 5000 * Math.pow(2, refreshAttempts - 1); // Backoff exponencial

        // Mantener las solicitudes pendientes para el próximo intento
        setTimeout(async () => {
          try {
            const newToken = await refreshAccessToken();
            // Si el reintento tiene éxito, resolver las solicitudes pendientes
            pendingRequests.forEach((req) => req.resolve(newToken));
            pendingRequests = [];
          } catch (retryError) {
            // Si el reintento falla, rechazar las solicitudes pendientes
            pendingRequests.forEach((req) => req.reject(retryError));
            pendingRequests = [];
          }
        }, backoffTime);

        return Promise.reject(error); // Propagar el error del intento actual
      } else {
        console.error('[Auth] Máximo de intentos de renovación alcanzado, cerrando sesión');
        pendingRequests.forEach((req) => req.reject(error));
        pendingRequests = [];
        cleanupAuthState(); // Usar la función centralizada
        return Promise.reject(error);
      }
    }
  };

  const fetchWithToken = async (url, options = {}) => {
    try {
      // Se espera que checkAndRenewToken gestione la renovación o devuelva un token válido.
      const accessToken = await checkAndRenewToken();
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      };

      const response = await fetch(url, options);

      if (response.status === 401 || response.status === 403) {
        console.warn(`[Auth] fetchWithToken recibió ${response.status}, intentando refrescar token.`);
        try {
          const newToken = await refreshAccessToken();
          const retryOptions = {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${newToken}`,
            },
          };
          return fetch(url, retryOptions);
        } catch (refreshError) {
          console.error("[Auth] Fallo al refrescar token después de un 401/403 en fetchWithToken:", refreshError);
          throw refreshError;
        }
      }

      return response;
    } catch (error) {
      console.error("[Auth] Error en fetchWithToken:", error);
      throw error;
    }
  };

  const checkAndRenewToken = async () => {
    const accessToken = tokenOps.account?.accessToken;

    // Si no hay accessToken, pero SÍ hay refreshToken, intentar refrescar
    if (!accessToken) {
      const refreshToken = tokenOps.root?.refreshToken || tokenOps.account?.refreshToken;
      if (refreshToken) {
        console.warn('[Auth] No hay accessToken en checkAndRenewToken, intentando refresh.');
        return refreshAccessToken();
      }
      console.error('[Auth] No hay tokens disponibles');
      return Promise.reject(new Error("No se encontró token de acceso ni de refresco."));
    }

    try {
      const token = jwt_decode(accessToken);
      const currentTime = Date.now();
      const expirationTime = token.exp * 1000;
      const timeRemaining = expirationTime - currentTime;

      // Verificar si el token está próximo a expirar
      if (timeRemaining < REFRESH_MARGIN_MS) {
        console.warn(`[Auth] Token expira en ${Math.round(timeRemaining / 1000)} segundos, renovando.`);
        return refreshAccessToken();
      }

      startRefreshTokenTimer(accessToken);
      return accessToken;
    } catch (error) {
      console.error('[Auth] Error al verificar token en checkAndRenewToken:', error);
      return refreshAccessToken();
    }
  };

  const checkAuth = async () => {
    try {
      await checkAndRenewToken();
      return Promise.resolve();
    } catch (error) {
      console.error('[Auth] checkAuth falló:', error);
      return Promise.reject();
    }
  };

  const checkError = async (error) => {
    const status = error.status || (error.response && error.response.status);

    if (status === 401 || status === 403) {
      console.warn(`[Auth] checkError interceptó un error ${status}.`);
      try {
        console.log("[Auth] Intentando renovar token desde checkError...");
        await refreshAccessToken();
        console.log("[Auth] Token renovado vía checkError. La petición original se reintentará.");
        return Promise.resolve();
      } catch (refreshError) {
        console.error('[Auth] Fallo crítico al renovar token durante checkError. Se procederá a logout:', refreshError.message);
        cleanupAuthState(); 
        return Promise.reject(refreshError); // Esto causará logout en React-Admin
      }
    }
    console.log(`[Auth] checkError recibió un error con status ${status || 'desconocido'} (${error.message || error}). No se considera error de autenticación.`);
    return Promise.resolve();
  };

  return {
    login: async ({ username, password, remember = false }) => {
      try {
        cleanupAuthState();
        const response = await fetch(`${hostname}/auth/access`, {
          method: "POST",
          body: JSON.stringify({ email: username, password, remember }),
          headers: new Headers({ "Content-Type": "application/json" }),
        });

        if (!response.ok) {
          throw new Error(response.statusText || 'Error al iniciar sesión');
        }

        const auth = await response.json();

        // Asegurar que el estado de autenticación esté limpio
        isRefreshing = false;
        pendingRequests = [];
        refreshAttempts = 0;

        // Guardar el nuevo token
        tokenOps.setRootToken(auth);

        const decodedToken = jwt_decode(auth.accessToken);
        if (!decodedToken.roles.includes("ADMIN")) {
          console.error("[Auth] El usuario no tiene rol ADMIN");
          cleanupAuthState();
          throw new Error("Roles incorrectos");
        }

        // Iniciar el temporizador de renovación
        startRefreshTokenTimer(auth.accessToken);
        return Promise.resolve();
      } catch (error) {
        console.error("[Auth] Error en inicio de sesión:", error);
        cleanupAuthState();
        throw error;
      }
    },

    fetchWithToken,

    checkAuth,

    getPermissions: () => {
      const accessToken = tokenOps.root?.accessToken || tokenOps.account?.accessToken;
      if (accessToken) {
        try {
          const decodedToken = jwt_decode(accessToken);
          const roles = decodedToken.roles;
          if (!roles) {
            console.warn('[Auth] No hay roles en el token decodificado');
            return Promise.reject(new Error("No hay roles disponibles en el token."));
          }
          return Promise.resolve({ roles });
        } catch (e) {
          console.warn('[Auth] Error decodificando token para permisos:', e);
          return Promise.reject(new Error("Error al obtener permisos, token inválido."));
        }
      }
      console.warn('[Auth] No hay accessToken para getPermissions');
      return Promise.reject(new Error("No hay sesión activa para obtener permisos."));
    },

    checkError,

    logout: () => {
      cleanupAuthState();
      return Promise.resolve();
    },
  };
};

export default authProvider;
